import {
  type ColumnFiltersState,
  type ColumnSizingState,
  type ExpandedState,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getGroupedRowModel,
  getSortedRowModel,
  type GroupingState,
  type OnChangeFn,
  type RowSelectionState,
  type SortingState,
  useReactTable,
  type VisibilityState,
} from "@tanstack/react-table";
import { type Dispatch, type SetStateAction, useCallback, useMemo, useState } from "react";
import {
  DEFAULT_COLUMN_ORDER,
  DEFAULT_COLUMN_VISIBILITY,
  type EntriesViewMeta,
  getEntriesColumns,
} from "@/features/attorney/views/Entries/columns";
import { EntriesTable } from "@/features/attorney/views/Entries/EntriesTable";
import type { Entry as TableEntry, EntryStatus } from "@/features/attorney/views/Entries/normalize";
import type { Matter } from "@/shared/cache";
import { formatEntryId, parseEntryId } from "@/v2/hooks/actions/entryId";

export type SortingMode = "time" | "matter";
export type SortDirection = "asc" | "desc";
export type InternalEntry = {
  id: number;
  status: EntryStatus;
  date: string;
  matter: string;
  matter_id?: string;
  matter_name?: string;
  client?: string;
  description: string;
  task: string;
  activity?: string;
  document_name?: string;
  duration: number;
  units_seconds?: number;
  start_time?: string;
  end_time?: string;
  application?: string;
  timekeeperId?: string | null;
  billable?: boolean | null;
  taskRequired?: boolean;
  activityRequired?: boolean;
  approved?: boolean;
  needs_improvement?: boolean;
  attorney_feedback?: string | null;
};

type InternalEntriesTableProps = {
  entries: InternalEntry[];
  matters: Matter[];
  checkedRows: Set<number>;
  setCheckedRows: Dispatch<SetStateAction<Set<number>>>;
  isLoading?: boolean;
  onRowClick?: (entry: InternalEntry) => void;
  sortingMode: SortingMode;
  sortDirection: SortDirection;
  emptyMessage?: string;
};

function toIsoDate(value: string): string {
  if (!value) return "";
  const datePart = value.split("T")[0];
  if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return datePart;

  const slash = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slash) {
    const [, month, day, year] = slash;
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function toTableEntry(entry: InternalEntry, mattersById: Map<string, Matter>): TableEntry {
  const matter = entry.matter_id ? mattersById.get(entry.matter_id) : undefined;
  // Requiredness is computed server-side; the admin payload omits it. Default to
  // false rather than re-deriving from the matter group, which drifts from policy.
  const taskRequired = entry.taskRequired ?? false;
  const activityRequired = entry.activityRequired ?? false;
  return {
    object: "entry",
    id: formatEntryId(entry.id),
    status: entry.status,
    date: toIsoDate(entry.date),
    startTime: entry.start_time ?? null,
    endTime: entry.end_time ?? null,
    units: entry.duration,
    unitsSeconds: entry.units_seconds ?? Math.round(entry.duration * 3600),
    description: entry.description || "",
    application: entry.application ?? null,
    documentName: entry.document_name ?? null,
    billable: entry.billable ?? null,
    billingCategory: matter?.billing_category ?? "unknown",
    billableCategory:
      matter?.billable_category ??
      (entry.billable === true
        ? "billable"
        : entry.billable === false
          ? "non_billable"
          : "unknown"),
    matter: entry.matter_id || entry.matter || entry.matter_name
      ? {
          id: entry.matter_id ?? null,
          code: entry.matter || matter?.matter || null,
          description: entry.matter_name || matter?.description || null,
          client: entry.client || matter?.client || null,
        }
      : null,
    codes: {
      task: entry.task || null,
      activity: entry.activity || null,
    },
    taskRequired,
    activityRequired,
  };
}

function rowSelectionFromIds(ids: Set<number>): RowSelectionState {
  const selection: RowSelectionState = {};
  for (const id of ids) selection[formatEntryId(id)] = true;
  return selection;
}

function parseRowId(rowId: string): number | null {
  try {
    return parseEntryId(rowId);
  } catch {
    const fallback = Number(rowId);
    return Number.isFinite(fallback) ? fallback : null;
  }
}

function initialSorting(mode: SortingMode, direction: SortDirection): SortingState {
  return [{ id: mode === "matter" ? "matter" : "date", desc: direction === "desc" }];
}

export function InternalEntriesTable({
  entries,
  matters,
  checkedRows,
  setCheckedRows,
  isLoading = false,
  onRowClick,
  sortingMode,
  sortDirection,
  emptyMessage,
}: InternalEntriesTableProps) {
  const [sorting, setSorting] = useState<SortingState>(() => initialSorting(sortingMode, sortDirection));
  const [expanded, setExpanded] = useState<ExpandedState>(true);
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});

  const mattersById = useMemo(
    () => new Map(matters.map((matter) => [matter.matter_id, matter])),
    [matters],
  );
  const data = useMemo(() => entries.map((entry) => toTableEntry(entry, mattersById)), [entries, mattersById]);
  const entriesByTableId = useMemo(() => {
    const map = new Map<string, InternalEntry>();
    for (const entry of entries) map.set(formatEntryId(entry.id), entry);
    return map;
  }, [entries]);

  const rowSelection = useMemo(() => rowSelectionFromIds(checkedRows), [checkedRows]);
  const handleRowSelectionChange = useCallback<OnChangeFn<RowSelectionState>>(
    (updater) => {
      setCheckedRows((previousIds) => {
        const previous = rowSelectionFromIds(previousIds);
        const nextSelection = typeof updater === "function" ? updater(previous) : updater;
        const next = new Set<number>();
        for (const [rowId, selected] of Object.entries(nextSelection)) {
          if (!selected) continue;
          const id = parseRowId(rowId);
          if (id !== null) next.add(id);
        }
        return next;
      });
    },
    [setCheckedRows],
  );

  const columns = useMemo(() => getEntriesColumns(null), []);
  const columnVisibility = useMemo<VisibilityState>(() => DEFAULT_COLUMN_VISIBILITY, []);
  const columnOrder = useMemo(
    () => [
      "_select",
      ...DEFAULT_COLUMN_ORDER,
      "client",
      "task",
      "activity",
      "matterName",
      "billable",
      "unitsSeconds",
    ],
    [],
  );
  const columnFilters = useMemo<ColumnFiltersState>(() => [], []);
  const sortBy = sorting[0]?.id ?? (sortingMode === "matter" ? "matter" : "date");
  const grouping = useMemo<GroupingState>(() => [sortBy], [sortBy]);
  const handleSortingChange = useCallback<OnChangeFn<SortingState>>((updater) => {
    setExpanded(true);
    setSorting((previous) => (typeof updater === "function" ? updater(previous) : updater));
  }, []);

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      grouping,
      expanded,
      columnVisibility,
      columnOrder,
      rowSelection,
      columnFilters,
      columnSizing,
    },
    onSortingChange: handleSortingChange,
    onExpandedChange: setExpanded,
    onRowSelectionChange: handleRowSelectionChange,
    onColumnSizingChange: setColumnSizing,
    enableRowSelection: (row) => !row.getIsGrouped(),
    enableGrouping: true,
    autoResetExpanded: false,
    enableColumnResizing: true,
    groupedColumnMode: false,
    columnResizeMode: "onEnd",
    defaultColumn: { minSize: 40, maxSize: 800 },
    getRowId: (row) => row.id,
    getCoreRowModel: getCoreRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    meta: {} satisfies EntriesViewMeta,
  });

  const handleTableRowClick = useCallback(
    (entry: TableEntry) => {
      const original = entriesByTableId.get(entry.id);
      if (original) onRowClick?.(original);
    },
    [entriesByTableId, onRowClick],
  );

  return (
    <EntriesTable
      table={table}
      isLoading={isLoading}
      rowSelection={rowSelection}
      onRowClick={onRowClick ? handleTableRowClick : undefined}
      emptyMessage={emptyMessage}
    />
  );
}
